<script lang="ts">
  import { push } from "svelte-spa-router";
  import {
    getWithJwt,
    serverlessRoutes,
    storage,
    translate,
    errorTracker,
  } from "lib";
  import type { ReengageClient, RiskLabel } from "../../interfaces/Reengage";
  import { relativeTime } from "../../lib/relativeTime";
  import { trainerChatWith } from "../../stores/trainerStores";
  import Svg from "../UI/Svg.svelte";

  export let client: ReengageClient;

  // Guards against a second tap while the chat session is being resolved.
  let isOpeningChat = false;

  const goToProfile = (): void => {
    push(`/profile/${client.client_id}`);
  };

  // The Reengage payload has no sessionId, so resolve it the same way the
  // profile screen does before handing off to the chat screen.
  const goToChat = async (): Promise<void> => {
    if (isOpeningChat) return;
    isOpeningChat = true;

    try {
      const res = await getWithJwt(
        `${serverlessRoutes.SESSION}/session-with-user/${client.client_id}`,
      );

      $trainerChatWith = {
        clientId: client.client_id,
        clientName: client.name,
        avatarUrl: null,
        sessionId: res?.data?.sessionId,
      };

      await storage.set("trainerChatWith", $trainerChatWith);
      await push("/chat");
    } catch (err) {
      errorTracker.captureError(err as Error | string, "error", {
        context: "reengage_client_card",
        action: "open_chat",
        clientId: client.client_id,
      });
    } finally {
      isOpeningChat = false;
    }
  };

  // Pill styling per risk level. Teal is the app's accent, used for Low.
  const riskClasses: Record<RiskLabel, string> = {
    High: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200",
    Medium: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200",
    Low: "bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-200",
  };

  // Pill label key per risk level. The API returns "High" | "Medium" | "Low";
  // map it to a translatable key so Serbian users see "Visok rizik" etc.
  const riskLabelKeys: Record<RiskLabel, string> = {
    High: "REENGAGE_RISK_HIGH",
    Medium: "REENGAGE_RISK_MEDIUM",
    Low: "REENGAGE_RISK_LOW",
  };

  $: pillClass = riskClasses[client.risk_label] ?? riskClasses.Low;
  $: riskLabel = translate(riskLabelKeys[client.risk_label] ?? riskLabelKeys.Low);

  // `missed_planned_workouts` is always null in this MVP (no scheduled-workout
  // data source yet) — show a dash rather than a misleading number.
  $: missedLabel =
    client.missed_planned_workouts == null
      ? "—"
      : String(client.missed_planned_workouts);
</script>

<article
  class="w-full px-4 py-3 border-b border-slate-200 dark:border-zinc-700 text-slate-900 dark:text-slate-100"
>
  <div class="flex items-start gap-2">
    <div class="flex items-center gap-2 flex-wrap min-w-0">
      <span class={`px-2 py-0.5 rounded-full text-xs font-semibold ${pillClass}`}>
        {riskLabel}
      </span>
      <span class="font-semibold text-base break-words">{client.name}</span>
    </div>

    <div class="ml-auto flex items-center gap-1 shrink-0">
      <button
        type="button"
        class="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors"
        aria-label={translate("PROFILE")}
        title={translate("PROFILE")}
        on:click={goToProfile}
      >
        <Svg name="user" size={20} />
      </button>
      <button
        type="button"
        class="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
        aria-label={translate("CHAT")}
        title={translate("CHAT")}
        disabled={isOpeningChat}
        on:click={goToChat}
      >
        <Svg name="message" size={20} />
      </button>
    </div>
  </div>

  <div class="mt-0.5 text-sm font-medium text-slate-600 dark:text-slate-300">
    {translate("SCORE")}: {client.risk_score}
  </div>

  <dl class="mt-2 text-xs text-slate-600 dark:text-slate-400 space-y-1">
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_SEEN_ONLINE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_seen_online_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_WORKOUT")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_workout_completed_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("MISSED_WORKOUTS")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">{missedLabel}</dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("USUAL_WORKOUTS_PER_WEEK")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {client.baseline_weekly_workouts}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("THIS_WEEK")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {client.current_week_workouts}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_CLIENT_MESSAGE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_client_message_at)}
      </dd>
    </div>
    <div class="flex justify-between gap-3">
      <dt>{translate("LAST_TRAINER_RESPONSE")}</dt>
      <dd class="text-right text-slate-800 dark:text-slate-200">
        {relativeTime(client.last_trainer_response_at)}
      </dd>
    </div>
    {#if client.last_checkin_at}
      <div class="flex justify-between gap-3">
        <dt>{translate("LAST_CHECKIN")}</dt>
        <dd class="text-right text-slate-800 dark:text-slate-200">
          {relativeTime(client.last_checkin_at)}
        </dd>
      </div>
    {/if}
  </dl>

  <p class="mt-2 text-xs text-slate-700 dark:text-slate-300">
    <span class="font-semibold">{translate("REASON")}:</span>
    {client.reason}
  </p>
</article>
